/**
 * CollabReadView — read-mode HTML render for collab-enabled posts.
 *
 * Push-only path (2026-05-29): opens a passive Yjs WebSocket against
 * `/api/v1/ws/posts/<id>` (same endpoint the Editor uses, but without
 * y-tiptap binding), keeps a local Y.Doc mirror, and re-derives the
 * materialized text + structured paragraph list whenever Yjs notifies
 * us of a change. No HTTP polling.
 *
 * Previously this polled `/crdt/text` + `/crdt/structured` every 8 s.
 *
 * Author-Highlight Toggle (V2.7.3, 2026-05-20): an opt-in button shows
 * a colored left-border + tooltip per paragraph attributing each block
 * to its writer. Off by default — clean markdown view stays default.
 * Choice persisted in localStorage.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { CONTENT_API, CONTENT_WS } from '../config'
import { getAccessToken, getUser } from '../auth'
import type { Awareness } from 'y-protocols/awareness'
import { colorFromId } from './colorFromId'
import { ReadModePresence } from './ReadModePresence'
import type { Post } from '../types'
import { AnnotationTooltip } from './AnnotationTooltip'
import { useMermaidAsync } from '../hooks/useMermaidAsync'
import { useD2Async } from '../hooks/useD2Async'
import { InlineSectionRenderer } from './InlineSectionRenderer'
import { annotationsFromSections } from './annotationsFromSections'
import { ReadModeAnnotationOverlay } from './ReadModeAnnotationOverlay'
import { DraftChangeMarks } from './DraftChangeMarks'
import { CompactAnnotationList } from './CompactAnnotationList'
import { CanvasCardEditor } from './CanvasCardEditor'
import type { AnnotationAnchor, ParagraphEditTarget } from './EditableParagraphs'
import { AnnotationMargin, type MarginItem } from './AnnotationMargin'
import { colorOf } from '../utils/annotationAuthors'
import type { ReservedBand } from './AnnotationMargin'
import {
  AnnotationFilterStyle,
  AnnotationLegend,
  AnnotationMinimap,
  AnnotationPanel,
} from './AnnotationTools'
import { filterEntries, useAnnotationKeys, useAnnotationTools } from '../hooks/useAnnotationTools'
import { entryKind, type AnnotationEntry } from '../utils/annotationEntries'
import { useAuthorColors } from '../hooks/useAuthorColors'
import { useSignedMedia } from '../hooks/useSignedMedia'
import { useLayoutMode } from '../hooks/useLayoutMode'
import { useWideViewport } from '../hooks/useWideViewport'
import { canCommentPost, canEditPost } from '../lib/access'

interface Props {
  post: Post
  /** `tocSource`: ganzes Dokument, wenn `markdown` nur ein Ausschnitt ist. */
  renderHtml: (markdown: string, tocSource?: string) => string
  /** Legacy prop — ignored, kept for callsite-compat. The view is
   *  push-driven now (Yjs WebSocket), no polling interval needed. */
  pollMs?: number
  /** External toggle from the meta-row "Autoren" chip. When undefined,
   *  CollabReadView falls back to its own localStorage-managed state.
   *  Plumbing this from the parent lets PostEditor host the toggle UI
   *  in the meta-row alongside Author / Type / Notify chips. */
  showAuthors?: boolean
  /** Optional handler — clicking the "Antworten"-Button on a
   *  Question-section card calls this with the section_id. Parent
   *  (PostEditor) typically opens a SectionReplyModal in response. */
  onReplyToSection?: (sectionId: string) => void
  /** P1.4: viewing user's display name — passed through to the
   *  section renderer so it can decide Lead/CC visuals. */
  currentUserName?: string
  /** Eingebettet im Portal (#2574): nur Text, Anmerken und Anmerkungen,
   *  ohne Legende, Übersicht und Panel der Markierungen. */
  minimal?: boolean
  /** Fassung, die der Mensch zuletzt gesehen hat; Neueres wird markiert. */
  changesSince?: number
}

interface StructuredParagraph {
  index: number
  text: string
  author_id?: string | null
  author_name?: string | null
  author_color?: string | null
  is_heading?: boolean
  heading_level?: number | null
  block_id?: string | null
  // commit_id is the shared UUID the server stamps on every paragraph
  // produced by a single REST doc_apply_text call. Frontend uses it
  // to draw one continuous author-stripe across the whole commit
  // instead of one stripe per paragraph (Alex 2026-05-30: stripes
  // pro Paragraph sind verwirrend, ein Push = eine Linie).
  commit_id?: string | null
  created_at?: string | null
  section_id?: string | null
  section_type?: 'product' | 'question' | 'task' | 'annotation' | null
  section_role?: 'start' | 'content' | 'reply' | 'end' | null
  section_attrs?: Record<string, unknown> | null
}

const HIGHLIGHT_STORAGE_KEY = 'collab_show_authors'

/**
 * Materialize a Yjs XmlFragment into the same shape `/crdt/text` and
 * `/crdt/structured` return, so the existing render-paths just work.
 * Mirrors the server's logic in routes/crdt_apply.py (strip tags, join
 * paragraphs with \n).
 */
function materializeFromYjs(
  yfrag: Y.XmlFragment,
): { text: string; paragraphs: StructuredParagraph[] } {
  const paragraphs: StructuredParagraph[] = []
  const lines: string[] = []
  let i = 0
  // Recursive text extraction: walks XmlText children and concatenates
  // their values verbatim. Same approach as the server's
  // extract_text_content helper. Previous impl regex-stripped
  // `<[^>]+>` which annihilated user-typed XML inside ```xml```
  // codeblocks (Alex 2026-05-30 Post 825: <gps_update> tags vanished).
  const extractText = (node: Y.XmlElement | Y.XmlText): string => {
    if (node instanceof Y.XmlText) return node.toString()
    // Zeilenumbruch-Knoten als \n, wie der Server seit fe591d8 (#2247).
    // Ohne das verklebte die Leseansicht Zeilen, die im Editor per
    // Shift+Enter getrennt waren.
    if (node.nodeName === 'hardBreak' || node.nodeName === 'hard_break') return '\n'
    const out: string[] = []
    for (const c of node.toArray()) {
      if (c instanceof Y.XmlText || c instanceof Y.XmlElement) {
        out.push(extractText(c))
      }
    }
    return out.join('')
  }

  yfrag.forEach((child) => {
    if (!(child instanceof Y.XmlElement)) return
    const attrs = child.getAttributes() as Record<string, string | undefined>
    const text = extractText(child)
    lines.push(text)
    let sectionAttrs: Record<string, unknown> | null = null
    if (typeof attrs.section_attrs === 'string' && attrs.section_attrs) {
      try {
        const parsed = JSON.parse(attrs.section_attrs)
        if (parsed && typeof parsed === 'object') {
          sectionAttrs = parsed as Record<string, unknown>
        }
      } catch {
        sectionAttrs = null
      }
    }
    const isHeadingByName =
      child.nodeName === 'heading' || child.nodeName === 'Heading'
    const headingMatch = text.match(/^(#{1,3})\s+\S/)
    const isHeading = isHeadingByName || !!headingMatch
    const headingLevel = headingMatch
      ? headingMatch[1].length
      : attrs.level
        ? parseInt(attrs.level, 10) || null
        : null
    paragraphs.push({
      index: i++,
      text,
      author_id: attrs.author_id ?? null,
      author_name: attrs.author_name ?? null,
      author_color: attrs.author_color ?? null,
      is_heading: isHeading,
      heading_level: headingLevel,
      block_id: attrs.block_id ?? null,
      commit_id: attrs.commit_id ?? null,
      created_at: attrs.created_at ?? null,
      section_id: attrs.section_id ?? null,
      section_type:
        (attrs.section_type as
          | 'product'
          | 'question'
          | 'task'
          | 'annotation'
          | undefined) ?? null,
      section_role:
        (attrs.section_role as
          | 'start'
          | 'content'
          | 'reply'
          | 'end'
          | undefined) ?? null,
      section_attrs: sectionAttrs,
    })
  })
  return { text: lines.join('\n'), paragraphs }
}

export function CollabReadView({
  post,
  renderHtml,
  showAuthors: externalShowAuthors,
  onReplyToSection,
  currentUserName,
  minimal = false,
  changesSince,
}: Props) {
  const [text, setText] = useState<string | null>(null)
  const [structured, setStructured] = useState<StructuredParagraph[] | null>(null)
  // Presence (2026-09-30): Awareness + Spiegel-Doc für „wer ist wo".
  const [presence, setPresence] = useState<{ awareness: Awareness; ydoc: Y.Doc } | null>(null)
  const authUser = getUser()
  const selfId = authUser ? `user:${authUser.id}` : 'anon'
  // Internal fallback (only used when no external prop provided).
  const [internalShowAuthors] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HIGHLIGHT_STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })
  const showAuthors = externalShowAuthors !== undefined ? externalShowAuthors : internalShowAuthors

  useEffect(() => {
    const ydoc = new Y.Doc()
    const token = getAccessToken()
    const baseUrl = `${CONTENT_WS}/api/v1/ws/posts/${post.id}`
    const params: Record<string, string> = {}
    if (token) params.token = token
    const provider = new WebsocketProvider(baseUrl, 'crdt', ydoc, {
      connect: true,
      params,
      protocols: token ? [`bearer.${token}`] : [],
    } as unknown as ConstructorParameters<typeof WebsocketProvider>[3])

    const yfrag = ydoc.get('content', Y.XmlFragment as unknown as new () => Y.XmlFragment) as Y.XmlFragment

    // Eigene Anwesenheit melden — ohne Cursor, mit mode 'read', damit
    // Editoren „liest mit" statt „tippt mit dir" anzeigen.
    if (authUser) {
      provider.awareness.setLocalStateField('user', {
        id: selfId,
        name: `${authUser.first_name} ${authUser.last_name}`.trim() || authUser.email,
        color: colorFromId(authUser.id),
        mode: 'read',
      })
    }
    // Nicht synchron im Effekt setzen (react-hooks-Regel) — nach dem Tick.
    queueMicrotask(() => setPresence({ awareness: provider.awareness, ydoc }))

    const recompute = () => {
      const result = materializeFromYjs(yfrag)
      setText((prev) => (prev === result.text ? prev : result.text))
      setStructured((prev) => {
        if (!prev || prev.length !== result.paragraphs.length) return result.paragraphs
        for (let i = 0; i < prev.length; i++) {
          const a = prev[i]
          const b = result.paragraphs[i]
          if (
            a.text !== b.text ||
            a.author_id !== b.author_id ||
            a.section_id !== b.section_id ||
            a.section_role !== b.section_role ||
            a.block_id !== b.block_id
          ) {
            return result.paragraphs
          }
        }
        return prev
      })
    }
    yfrag.observeDeep(recompute)
    // First materialize after the initial sync round-trip. y-websocket
    // emits 'sync' once the server-state is applied.
    provider.on('sync', recompute)
    // Best-effort initial recompute too — yfrag may already have
    // content if the editor was open on another tab + Yjs persisted.
    recompute()

    return () => {
      setPresence(null)
      yfrag.unobserveDeep(recompute)
      provider.destroy()
      ydoc.destroy()
    }
    // post.id is the only intentional dependency — token is read fresh
    // at provider-construct time, and showAuthors does not affect the
    // subscription itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id])

  // Issue #15 follow-up: ref-as-state so AnnotationTooltip can attach
  // delegated hover-listeners on the read-mode markdown container.
  // Also drives `useMermaidAsync` which renders erDiagram /
  // sequenceDiagram / classDiagram placeholders that beautiful-mermaid
  // can't handle (Alex 2026-05-27 — empty entity boxes on Post #814).
  const [readModeRoot, setReadModeRoot] = useState<HTMLElement | null>(null)
  useMermaidAsync(readModeRoot)
  useD2Async(readModeRoot)

  // Absatz-Edit im Lesemodus (2026-09-30): Doppelklick/Stift auf einem
  // Absatz öffnet den CanvasCardEditor-Popover über genau diesem Absatz.
  // Der Popover wird erst beim ersten Edit gemountet (eigene WS-Verbindung
  // nur, wenn jemand wirklich schreibt) und danach für die Lebensdauer der
  // Ansicht behalten — schneller zweiter Edit, kein erneuter Sync.
  const [editTarget, setEditTarget] = useState<ParagraphEditTarget | null>(null)
  const [editorMounted, setEditorMounted] = useState(false)
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
  const editingBlockId = editTarget?.blockId ?? null
  useEffect(() => {
    if (!editingBlockId) {
      queueMicrotask(() => setAnchorRect(null))
      return
    }
    const measure = () => {
      const el = document.querySelector(
        `.read-para[data-block-id="${cssEsc(editingBlockId)}"]`,
      )
      if (el) setAnchorRect(el.getBoundingClientRect())
    }
    const raf = window.requestAnimationFrame(measure)
    const intv = window.setInterval(measure, 120)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearInterval(intv)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [editingBlockId])
  // Der Server entscheidet; hier nur, was angeboten wird (lib/access).
  const canEdit = !!getAccessToken() && canEditPost(post)
  const canComment = !!getAccessToken() && canCommentPost(post)
  const onParagraphEdit = canEdit
    ? (t: ParagraphEditTarget) => {
        // Rect sofort mitgeben (Event-Handler, kein Effekt): der Popover
        // ist damit im selben Commit sichtbar, in dem der Editor
        // gemountet wird — sonst trifft der Fokus-Versuch einen noch
        // unsichtbaren Container und scheitert still.
        setAnchorRect(t.element.getBoundingClientRect())
        setEditorMounted(true)
        setEditTarget(t)
      }
    : undefined

  // Two-step grouping for the authors-on view:
  //  1. Atoms: a fenced code-block (mermaid/d2/etc.) spans multiple
  //     paragraphs because of the \n\n chunk-split. We bundle all the
  //     paragraphs between matching ``` fences so marked() sees the
  //     whole block — otherwise diagrams render as raw fragments.
  //  2. Commit-groups: atoms that share (author_id, commit_id) become
  //     a single visual block with ONE continuous left-border.
  //     commit_id is the server-stamped UUID shared across every
  //     paragraph created by a single doc_apply_text call. Without
  //     this grouping the user sees N stripes for one "send" — Alex
  //     2026-05-30: "da kommt ja auf einmal alles gepusht, ich will
  //     EINE linie pro push, nicht pro paragraph".
  // (The legacy buildAtoms / buildCommitGroups / renderAtomParagraph /
  // renderHighlighted helpers were removed alongside the highlight-mode
  // render — InlineSectionRenderer is now the single read-view path
  // and computes its own commit grouping internally. Removed in the
  // follow-up that turns `npm run build` green from clean ecc9601.)

  // Anmerkungen am Seitenrand (2026-10-02): Anmerkungs-Sections werden
  // als Karten in einer Randspalte gezeigt, ausgerichtet an ihrer im Text
  // markierten Passage — statt als Karten mitten im Textfluss. Auf
  // schmalen Fenstern bleibt die bisherige Darstellung (Karten im Fluss +
  // Hover-Karte); die Markierung im Text gibt es in beiden Fällen.
  const wide = useWideViewport(1180)
  const [layoutMode] = useLayoutMode()
  const veryWide = useWideViewport(1500)
  const twoSidedMargin = layoutMode !== 'wide' && veryWide
  const [composingBand, setComposingBand] = useState<ReservedBand | null>(null)
  const sectionAnnotations = useMemo(
    () => (structured ? annotationsFromSections(structured) : []),
    [structured],
  )
  // Lesewerkzeuge für Markierungen (#2267). Eine Anmerkungs-Section ohne
  // Text ist eine Markierung: gepunktete Linie, keine Karte.
  const colorsVersion = useAuthorColors(post.id)
  // Private Bilder im Lesetext über signierte Adressen nachladen (#5235).
  useSignedMedia(post.id, readModeRoot)
  const annotationTools = useAnnotationTools()
  const [annotateSignal, setAnnotateSignal] = useState(0)
  const [highlightSignal, setHighlightSignal] = useState(0)
  const entries = useMemo<AnnotationEntry[]>(
    () =>
      sectionAnnotations.map((a) => ({
        id: a.id,
        note_text: a.note_text,
        author_id: a.author_id,
        author_name: a.author_name,
        created_at: a.created_at,
        replies: a.replies || [],
        kind: entryKind(a.note_text),
        quote: a.snippet || '',
      })),
    [sectionAnnotations],
  )
  const visibleEntries = useMemo(
    () => filterEntries(entries, annotationTools.author),
    [entries, annotationTools.author],
  )
  const hiddenEntryIds = useMemo(
    () => entries.filter((e) => !visibleEntries.includes(e)).map((e) => e.id),
    [entries, visibleEntries],
  )
  const highlightSectionIds = useMemo(
    () => new Set(entries.filter((e) => e.kind === 'highlight').map((e) => String(e.id))),
    [entries],
  )
  const annotationAnchors = useMemo<AnnotationAnchor[]>(() => {
    if (!structured) return []
    const out: AnnotationAnchor[] = []
    for (const p of structured) {
      if (p.section_type !== 'annotation' || p.section_role !== 'start' || !p.section_id) continue
      const attrs = (p.section_attrs || {}) as Record<string, unknown>
      const blockId = typeof attrs.anchor_paragraph_id === 'string' ? attrs.anchor_paragraph_id : ''
      const quote = typeof attrs.anchor_quote === 'string' ? attrs.anchor_quote : ''
      if (!blockId || !quote) continue
      const meta = sectionAnnotations.find((a) => String(a.id) === String(p.section_id))
      const rawStart = Number(attrs.anchor_start)
      out.push({
        id: p.section_id,
        blockId,
        quote,
        anchorStart: Number.isFinite(rawStart) ? rawStart : undefined,
        color: colorOf(meta?.author_id, meta?.author_name),
        kind: entryKind(meta?.note_text),
      })
    }
    return out
    // colorsVersion: zugewiesene Farben kommen nach dem ersten Rendern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structured, sectionAnnotations, colorsVersion])
  const marginItems = useMemo<MarginItem[]>(
    () => visibleEntries.filter((e) => e.kind === 'note'),
    [visibleEntries],
  )
  // Eingebettet keine Randspalte: Anmerkungen stehen kompakt darunter.
  const showMargin = !minimal && wide && marginItems.length > 0
  const requestHighlight = useCallback(() => setHighlightSignal((n) => n + 1), [])
  const requestAnnotate = useCallback(() => setAnnotateSignal((n) => n + 1), [])
  useAnnotationKeys({
    root: readModeRoot,
    entries: visibleEntries,
    enabled: true,
    onHighlight: canComment ? requestHighlight : undefined,
    onAnnotate: canComment ? requestAnnotate : undefined,
  })
  const replyToAnnotation = async (item: MarginItem, replyText: string): Promise<boolean> => {
    const token = getAccessToken()
    if (!token) return false
    try {
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${post.id}/sections/${encodeURIComponent(String(item.id))}/reply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ text: replyText }),
        },
      )
      if (!r.ok) {
        alert(`Fehler beim Speichern: ${r.status} ${await r.text()}`)
        return false
      }
      return true // der Yjs-Spiegel bringt die Antwort von selbst
    } catch (e) {
      alert(`Netzwerk-Fehler: ${e instanceof Error ? e.message : String(e)}`)
      return false
    }
  }

  // Fall back to snapshot content while the first fetch is in flight.
  const md = text !== null ? text : post.content || ''
  // Abschnitte und Absätze werden einzeln gerendert; ein {{TOC}} darin
  // braucht trotzdem die Überschriften des ganzen Dokuments.
  const renderPart = (part: string) => renderHtml(part, md)

  return (
    <>
      <ReadModePresence
        awareness={presence?.awareness ?? null}
        ydoc={presence?.ydoc ?? null}
        selfId={selfId}
        readModeRoot={readModeRoot}
      />
      <AnnotationFilterStyle hiddenIds={hiddenEntryIds} />
      {!minimal && (
        <>
          <AnnotationLegend entries={entries} tools={annotationTools} twoSided={twoSidedMargin && showMargin} />
          <AnnotationMinimap root={readModeRoot} entries={visibleEntries} />
          <AnnotationPanel root={readModeRoot} entries={entries} tools={annotationTools} title={post.title} />
        </>
      )}
      {structured ? (
        <div className={showMargin ? `read-with-margin${twoSidedMargin ? ' read-with-margin--two' : ''}` : undefined}>
        <div ref={(el) => setReadModeRoot(el)} className="editor-content">
          <InlineSectionRenderer
            paragraphs={structured}
            renderMarkdown={renderPart}
            onReply={onReplyToSection}
            postId={post.id}
            notifyAuthors={
              ((post.metadata_json as Record<string, unknown> | undefined)
                ?.notify_authors as string[] | undefined) || []
            }
            showAuthors={showAuthors}
            currentUserName={currentUserName}
            onParagraphEdit={onParagraphEdit}
            editingBlockId={editingBlockId}
            annotationAnchors={annotationAnchors}
            hideAnnotationSections={showMargin || minimal}
            hiddenSectionIds={highlightSectionIds}
            canWrite={canEdit}
            onSectionCreated={() => {
              /* Yjs WS pushes the new section into the local Y.Doc —
                 our materializeFromYjs effect re-runs automatically,
                 so no manual refetch is needed. */
            }}
          />
        </div>
        {showMargin && (
          <AnnotationMargin
            root={readModeRoot}
            items={marginItems}
            onReply={canComment ? replyToAnnotation : undefined}
            twoSided={twoSidedMargin}
            sideMode={annotationTools.sideMode}
            reserved={composingBand}
          />
        )}
        </div>
      ) : (
        <div
          ref={(el) => setReadModeRoot(el)}
          className="editor-content"
          dangerouslySetInnerHTML={{ __html: renderHtml(md) }}
        />
      )}
      {minimal && structured && (
        <CompactAnnotationList paragraphs={structured} annotations={sectionAnnotations} />
      )}
      {changesSince !== undefined && (
        <DraftChangeMarks
          postId={post.id}
          sinceVersion={changesSince}
          root={readModeRoot}
          docKey={structured}
        />
      )}
      {!showMargin && (
        <AnnotationTooltip
          editorRoot={readModeRoot}
          annotations={[
            ...((post.annotations || []) as any),
            ...sectionAnnotations.filter((a) => a.note_text.trim()),
          ]}
        />
      )}
      {/* Read-Mode "Anmerken"-Selektion (2026-06-12). Native window
          selection → resolveAnchor() findet das passende paragraph per
          substring-match → POST als annotation-Section (drift-stable). */}
      {canComment && (
        <ReadModeAnnotationOverlay
          postId={post.id}
          paragraphs={structured}
          readModeRoot={readModeRoot}
          openSignal={annotateSignal}
          highlightSignal={highlightSignal}
          onComposing={setComposingBand}
        />
      )}
      {editorMounted && (
        <CanvasCardEditor
          postId={post.id}
          editingBlockId={editingBlockId}
          editingParagraphIndex={editTarget?.paragraphIndex ?? null}
          anchorRect={anchorRect}
          maxWidth={1200}
          sourceSelector=".read-para"
          variant="read"
          mentionItems={
            ((post.metadata_json as Record<string, unknown> | undefined)
              ?.notify_authors as string[] | undefined) || []
          }
          onRequestClose={() => setEditTarget(null)}
        />
      )}
    </>
  )
}

function cssEsc(value: string): string {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value)
  return value.replace(/["\\]/g, '\\$&')
}
